import { ref } from 'vue'
import { createChart, CandlestickSeries, CrosshairMode } from 'lightweight-charts'
import { decodeMexcSpotKline } from '@/utils/mexcKlineProtobuf'

// ─── Referências de zoom / escala inicial ────────────────────────
// 64 velas visíveis de 15m (~16h) com 12 barras de espaço em branco à direita
const VISIBLE_BARS = 64
const RIGHT_SPACE = 12
const MEXC_WS_URL = 'wss://wbs-api.mexc.com/ws'
const MEXC_PING_MS = 15000
const DEFAULT_SYMBOL = 'BTCUSDT'
const DEFAULT_INTERVAL = '15m'

const INTERVAL_TO_MEXC = {
  '1m': 'Min1',
  '5m': 'Min5',
  '15m': 'Min15',
  '30m': 'Min30',
  '1h': 'Min60',
  '4h': 'Hour4',
  '8h': 'Hour8',
  '1d': 'Day1',
}

const CHART_OPTS = {
  autoSize: true,
  layout: {
    background: { color: 'var(--chart-bg, #161b22)' },
    textColor: '#c9d1d9',
    fontSize: 13,
  },
  grid: {
    vertLines: { color: '#21262d' },
    horzLines: { color: '#21262d' },
  },
  rightPriceScale: { borderColor: '#30363d' },
  timeScale: { borderColor: '#30363d', timeVisible: true, secondsVisible: false },
  crosshair: { mode: CrosshairMode.Normal },
}

export function useCandleChart(containerRef) {
  const chart = ref(null)
  const series = ref(null)
  const currentPrice = ref(null)
  const hoverPrice = ref(null)
  let lastHoverY = null

  const _pmpLine = ref(null)
  const _hedgeLine = ref(null)
  const _priceLine = ref(null)

  let wsInstance = null
  let pingTimer = null
  const isWsConnected = ref(false)

  /**
   * Histórico 15m / 7 dias via Binance REST (CORS no browser).
   * O ao vivo continua no WebSocket da MEXC.
   */
  async function fetchKlines15m() {
    try {
      const url = `https://api.binance.com/api/v3/klines?symbol=${DEFAULT_SYMBOL}&interval=${DEFAULT_INTERVAL}&limit=672`
      const res = await fetch(url)
      const raw = await res.json()

      if (!Array.isArray(raw)) {
        console.error('[useCandleChart] Resposta inesperada das klines Binance:', raw)
        return []
      }

      return raw.map(k => ({
        time: Math.floor(k[0] / 1000),
        open: parseFloat(k[1]),
        high: parseFloat(k[2]),
        low: parseFloat(k[3]),
        close: parseFloat(k[4]),
      }))
    } catch (err) {
      console.error('[useCandleChart] Erro ao buscar klines 15m da Binance:', err)
      return []
    }
  }

  function mexcKlineChannel(symbol, interval) {
    const pair = String(symbol || DEFAULT_SYMBOL).toUpperCase()
    const mexcInterval = INTERVAL_TO_MEXC[interval] || 'Min15'
    return `spot@public.kline.v3.api.pb@${pair}@${mexcInterval}`
  }

  function stopPing() {
    if (pingTimer) {
      clearInterval(pingTimer)
      pingTimer = null
    }
  }

  function startPing() {
    stopPing()
    pingTimer = setInterval(() => {
      if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
        wsInstance.send(JSON.stringify({ method: 'PING' }))
      }
    }, MEXC_PING_MS)
  }

  function applyLiveCandle(candle) {
    if (!series.value || !candle) return
    series.value.update(candle)
    currentPrice.value = candle.close
    updatePriceLine(candle.close)
  }

  /**
   * Conecta ao WebSocket público da MEXC (kline protobuf BTCUSDT 15m)
   */
  function connectWebSocket(symbol = DEFAULT_SYMBOL, interval = DEFAULT_INTERVAL) {
    if (wsInstance) {
      disconnectWebSocket()
    }

    const channel = mexcKlineChannel(symbol, interval)
    wsInstance = new WebSocket(MEXC_WS_URL)
    wsInstance.binaryType = 'arraybuffer'

    wsInstance.onopen = () => {
      isWsConnected.value = true
      wsInstance.send(JSON.stringify({
        method: 'SUBSCRIPTION',
        params: [channel],
      }))
      startPing()
      console.log('[useCandleChart] WebSocket MEXC conectado:', channel)
    }

    wsInstance.onmessage = (event) => {
      try {
        if (typeof event.data === 'string') {
          const msg = JSON.parse(event.data)
          if (msg.msg === 'PONG' || msg.code === 0) return
          return
        }

        const bytes = event.data instanceof ArrayBuffer
          ? new Uint8Array(event.data)
          : event.data
        const candle = decodeMexcSpotKline(bytes)
        if (candle) applyLiveCandle(candle)
      } catch (e) {
        console.error('[useCandleChart] Erro ao processar mensagem do WS MEXC:', e)
      }
    }

    wsInstance.onerror = (err) => {
      console.error('[useCandleChart] WebSocket MEXC erro:', err)
    }

    wsInstance.onclose = () => {
      stopPing()
      isWsConnected.value = false
      console.log('[useCandleChart] WebSocket MEXC desconectado')
    }
  }

  function disconnectWebSocket() {
    stopPing()
    if (wsInstance) {
      wsInstance.close()
      wsInstance = null
    }
    isWsConnected.value = false
  }

  /**
   * Inicializa o gráfico: histórico Binance BTCUSDT + WS MEXC se enableWs
   */
  async function init(pmpSpot = 0, hedgeAvgPrice = 0, enableWs = false) {
    if (!containerRef.value) return

    chart.value = createChart(containerRef.value, CHART_OPTS)
    chart.value.subscribeCrosshairMove((param) => {
      if (!param?.point || !series.value) {
        hoverPrice.value = null
        lastHoverY = null
        return
      }
      const y = param.point.y
      if (lastHoverY != null && Math.abs(y - lastHoverY) < 0.5 && hoverPrice.value != null) {
        return
      }
      lastHoverY = y
      const price = series.value.coordinateToPrice(y)
      hoverPrice.value = price != null && price > 0 ? price : null
    })

    series.value = chart.value.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    })

    const klines = await fetchKlines15m()
    if (klines.length > 0) {
      series.value.setData(klines)
      currentPrice.value = klines[klines.length - 1].close

      _priceLine.value = series.value.createPriceLine({
        price: currentPrice.value,
        color: '#3498db',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: `$${currentPrice.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      })
    }

    if (pmpSpot > 0) {
      _pmpLine.value = series.value.createPriceLine({
        price: pmpSpot,
        color: '#f39c12',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: 'PMP',
      })
    }

    if (hedgeAvgPrice > 0) {
      _hedgeLine.value = series.value.createPriceLine({
        price: hedgeAvgPrice,
        color: '#9b59b6',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: 'HedgeAvg',
      })
    }

    if (klines.length > 0) {
      const lastIdx = klines.length - 1
      chart.value.timeScale().setVisibleLogicalRange({
        from: lastIdx - VISIBLE_BARS + RIGHT_SPACE,
        to: lastIdx + RIGHT_SPACE,
      })
    }

    if (enableWs) {
      connectWebSocket()
    }
  }

  function updatePmpLine(price) {
    if (_pmpLine.value && price > 0) {
      _pmpLine.value.applyOptions({ price })
    }
  }

  function updateHedgeLine(price) {
    if (_hedgeLine.value && price > 0) {
      _hedgeLine.value.applyOptions({ price })
    }
  }

  function updatePriceLine(price) {
    if (_priceLine.value && price > 0) {
      _priceLine.value.applyOptions({
        price,
        title: `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      })
    }
  }

  function destroy() {
    disconnectWebSocket()
    hoverPrice.value = null
    lastHoverY = null
    if (chart.value) {
      chart.value.remove()
      chart.value = null
    }
  }

  return {
    chart,
    series,
    currentPrice,
    hoverPrice,
    isWsConnected,
    init,
    destroy,
    connectWebSocket,
    disconnectWebSocket,
    updatePmpLine,
    updateHedgeLine,
    updatePriceLine,
  }
}
